'use client';

import { useEffect, useState } from 'react';

import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useAppContext } from '@/context';
import { brandName } from '@/constants';
import { TAGBrand } from '@/types';

import {
	fieldStyles,
	formCardStyles,
	formTitleStyles,
	heroContentStyles,
	heroDescriptionStyles,
	heroEyebrowStyles,
	heroHeadingStyles,
	heroPhoneStyles,
	heroWrapperStyles,
	scheduleButtonStyles,
} from './PatientInfoWidget.styles';
import { maskDate, maskPhone, normalizeDateForBridge } from './PatientInfoWidget.helpers';

export interface PatientInfoWidgetProps {
	/** Eyebrow text. When `heading` is also provided, renders the full hero two-column layout. */
	eyebrow?: string;
	/** Main heading. When provided, renders the full hero two-column layout. */
	heading?: string;
	/** Body description shown in the hero content column. */
	description?: string;
	/** Phone number shown in the hero content column (e.g. "(800) 277-3633"). */
	phoneNumber?: string;
	/** HTML tag for the heading element. Defaults to 'h2' to avoid duplicate h1s when embedded in a page with an existing h1. */
	headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
	/** Title displayed above the form fields. */
	formTitle?: string;
	/** Label for the schedule button. */
	scheduleButtonText?: string;
	/** URL the schedule button navigates to after storing prefill payload in sessionStorage. */
	schedulingUrl: string;
}

interface FormValues {
	firstName: string;
	lastName: string;
	dateOfBirth: string;
	email: string;
	phone: string;
}

const INITIAL_VALUES: FormValues = {
	firstName: '',
	lastName: '',
	dateOfBirth: '',
	email: '',
	phone: '',
};

const CLEARCHOICE_BRAND_NAME = brandName[TAGBrand.ClearChoice];

export const HERO_FORM_PREFILL_SESSION_STORAGE_KEY = 'hero-form-prefill';
export const HERO_FORM_PREFILL_WINDOW_NAME_KEY = 'hero-form-prefill';

function savePrefillPayload(form: FormValues) {
	if (typeof window === 'undefined') return;

	const payload = {
		firstName: form.firstName,
		lastName: form.lastName,
		dateOfBirth: form.dateOfBirth,
		email: form.email,
		phone: form.phone.replace(/\D/g, ''),
	};

	globalThis.sessionStorage.setItem(HERO_FORM_PREFILL_SESSION_STORAGE_KEY, JSON.stringify(payload));

	const existingWindowName = globalThis.window.name;
	let nextWindowNamePayload: Record<string, unknown> = {};

	if (existingWindowName.trim().startsWith('{')) {
		try {
			const parsedWindowName = JSON.parse(existingWindowName);
			if (parsedWindowName && typeof parsedWindowName === 'object') {
				nextWindowNamePayload = parsedWindowName as Record<string, unknown>;
			}
		} catch {
			nextWindowNamePayload = {};
		}
	}

	nextWindowNamePayload[HERO_FORM_PREFILL_WINDOW_NAME_KEY] = payload;
	globalThis.window.name = JSON.stringify(nextWindowNamePayload);
}

export default function PatientInfoWidget({
	eyebrow,
	heading,
	description,
	phoneNumber,
	headingTag = 'h2',
	formTitle = 'Schedule a consultation',
	scheduleButtonText = 'Schedule consultation',
	schedulingUrl,
}: Readonly<PatientInfoWidgetProps>) {
	const [form, setForm] = useState<FormValues>(INITIAL_VALUES);
	const { config } = useAppContext();
	const theme = useTheme();
	const isClearChoice = config.name === CLEARCHOICE_BRAND_NAME;

	// Register the native-bridge contact-autofill handler so Facebook/Instagram
	// in-app browsers (or GTM scripts) can pre-fill the form fields.
	// The `if (!field) continue` guard is the critical fix for the crash:
	// "Cannot read properties of undefined (reading 'value')" — DIGITAL-MARKETING-1XW.
	useEffect(() => {
		window.setContactAutofillValuesFromBridge = (fields: unknown) => {
			if (!Array.isArray(fields)) return;

			const updates: Partial<FormValues> = {};
			for (const field of fields as Array<{ name?: string; value?: string } | null | undefined>) {
				if (!field) continue;

				const { name, value } = field;
				if (typeof name !== 'string' || typeof value !== 'string') continue;

				switch (name) {
					case 'first_name':
					case 'firstName':
						updates.firstName = value;
						break;
					case 'last_name':
					case 'lastName':
						updates.lastName = value;
						break;
					case 'email':
						updates.email = value;
						break;
					case 'phone':
					case 'tel':
					case 'phone_number':
						updates.phone = maskPhone(value);
						break;
					case 'bday':
					case 'dateOfBirth':
					case 'date_of_birth': {
						const normalized = normalizeDateForBridge(value);
						if (normalized) updates.dateOfBirth = normalized;
						break;
					}
				}
			}

			if (Object.keys(updates).length > 0) {
				setForm((prev) => ({ ...prev, ...updates }));
			}
		};

		return () => {
			delete window.setContactAutofillValuesFromBridge;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const allFilled = Object.values(form).every((v) => v.trim().length > 0);

	const handleChange = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
		let value = e.target.value;
		if (field === 'phone') value = maskPhone(value);
		if (field === 'dateOfBirth') value = maskDate(value);
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!allFilled) return;
		savePrefillPayload(form);
		globalThis.location.href = schedulingUrl;
	};

	const formCard = (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={formCardStyles}
			aria-label={formTitle}
			data-testid="patient-info-widget"
			noValidate
		>
			<Typography sx={formTitleStyles} data-testid="patient-info-widget-title">
				{formTitle}
			</Typography>

			<TextField
				label="First Name"
				required
				value={form.firstName}
				onChange={handleChange('firstName')}
				sx={fieldStyles}
				autoComplete="given-name"
				inputProps={{ 'data-testid': 'patient-info-first-name', 'aria-required': true }}
			/>
			<TextField
				label="Last Name"
				required
				value={form.lastName}
				onChange={handleChange('lastName')}
				sx={fieldStyles}
				autoComplete="family-name"
				inputProps={{ 'data-testid': 'patient-info-last-name', 'aria-required': true }}
			/>
			<TextField
				label="Birth Date"
				required
				value={form.dateOfBirth}
				onChange={handleChange('dateOfBirth')}
				placeholder="MM/DD/YYYY"
				sx={fieldStyles}
				autoComplete="bday"
				inputProps={{
					'data-testid': 'patient-info-dob',
					'aria-required': true,
				}}
			/>
			<TextField
				label="Email"
				required
				type="email"
				value={form.email}
				onChange={handleChange('email')}
				sx={fieldStyles}
				autoComplete="email"
				inputProps={{ 'data-testid': 'patient-info-email', 'aria-required': true }}
			/>
			<TextField
				label="Phone"
				required
				type="tel"
				value={form.phone}
				onChange={handleChange('phone')}
				sx={fieldStyles}
				autoComplete="tel"
				inputProps={{
					'data-testid': 'patient-info-phone',
					'aria-required': true,
				}}
			/>

			<Button
				type="submit"
				variant="contained"
				disabled={!allFilled}
				aria-disabled={!allFilled}
				sx={scheduleButtonStyles(isClearChoice, theme)}
				data-testid="patient-info-schedule-button"
			>
				{scheduleButtonText}
			</Button>
		</Box>
	);

	if (!heading) {
		return formCard;
	}

	return (
		<Box sx={heroWrapperStyles} data-testid="patient-info-hero">
			<Box sx={heroContentStyles}>
				{eyebrow && (
					<Typography sx={heroEyebrowStyles} data-testid="patient-info-hero-eyebrow">
						{eyebrow}
					</Typography>
				)}
				<Typography component={headingTag} sx={heroHeadingStyles} data-testid="patient-info-hero-heading">
					{heading}
				</Typography>
				{description && (
					<Typography sx={heroDescriptionStyles} data-testid="patient-info-hero-description">
						{description}
					</Typography>
				)}
				{phoneNumber && (
					<Box sx={heroPhoneStyles} data-testid="patient-info-hero-phone">
						<PhoneIcon fontSize="small" aria-hidden="true" />
						<span>{phoneNumber}</span>
					</Box>
				)}
			</Box>
			{formCard}
		</Box>
	);
}
